import {
  DEFAULT_SCENE_ANIMATIONS,
  extractAnimationFromEntry,
  filterValidSceneAnimationEntries,
} from "../blueprint/runtime/scene-animations.js";
import type { SceneAnimationEntry } from "../layout/types.js";
import { uniqueDependencies } from "./method.js";
import type {
  RuntimeAnimationPlayback,
  RuntimeAnimationResolver,
  RuntimeContext,
} from "./types.js";

function compileAnimationEntry(entry: SceneAnimationEntry): {
  dependencies: string[];
  evaluate: RuntimeAnimationResolver;
} {
  return {
    dependencies: [],
    evaluate(ctx: RuntimeContext): RuntimeAnimationPlayback | null {
      const payload = extractAnimationFromEntry(
        entry,
        ctx,
        ctx.__currentNodeContext,
      );

      if (!payload) {
        return null;
      }

      return { payload };
    },
  };
}

/**
 * Compiles scene catalog animations into runtime resolvers keyed by id and name.
 */
export function compileSceneAnimations(entries: SceneAnimationEntry[] = []): {
  dependencies: string[];
  animations: Record<string, RuntimeAnimationResolver>;
  animationsByName: Record<string, RuntimeAnimationResolver>;
} {
  const animations: Record<string, RuntimeAnimationResolver> = {};
  const animationsByName: Record<string, RuntimeAnimationResolver> = {};
  const dependencyBuckets: string[][] = [];

  for (const payload of DEFAULT_SCENE_ANIMATIONS) {
    const name = String(payload.name ?? "").trim();
    if (!name) {
      continue;
    }
    const resolver: RuntimeAnimationResolver = () => ({ payload });
    animationsByName[name] = resolver;
    animationsByName[name.toLocaleLowerCase()] = resolver;
  }

  for (const entry of filterValidSceneAnimationEntries(entries)) {
    const compiled = compileAnimationEntry(entry);

    animations[entry.id] = compiled.evaluate;
    animationsByName[entry.name] = compiled.evaluate;
    animationsByName[entry.name.toLocaleLowerCase()] = compiled.evaluate;
    dependencyBuckets.push(compiled.dependencies);
  }

  return {
    dependencies: uniqueDependencies(dependencyBuckets.flat()),
    animations,
    animationsByName,
  };
}
